import { Stack } from '@mui/material';
import { useEffect } from 'react';
import {
    BooleanInput,
    NumberInput,
    TextInput,
    required,
    useRecordContext,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import ImageEditorField from '@app/misc/ImageEditorField';
import { useResource } from '@app/misc/useResource';
import type { SupplyFormValues } from '@domain/supply';
import { showModeReadOnlySx } from '@app/root/theme';

export const SupplyInputs = ({ readOnly = false }: { readOnly?: boolean }) => {
    const record = useRecordContext<SupplyFormValues>();
    const { setValue } = useFormContext<SupplyFormValues>();
    const noPrice = useWatch<SupplyFormValues, 'noPrice'>({
        name: 'noPrice',
    });

    useEffect(() => {
        if (noPrice) setValue('price', null);
    }, [noPrice, setValue]);

    const { data: imageSrc } = useResource(record?.imageId);

    return (
        <Stack gap={2} p={1} sx={readOnly ? showModeReadOnlySx : undefined}>
            <TextInput
                source="name"
                label="Name"
                validate={required()}
                readOnly={readOnly}
            />
            <ImageEditorField
                source="imageInput"
                height={120}
                emptyText="Upload image"
                linkPosition="bottom"
                src={imageSrc ?? undefined}
                readOnly={readOnly}
            />
            <TextInput
                source="type"
                label="Type"
                helperText="e.g. Book, Home, Exhibitor kit (not for sale), Clothes"
                readOnly={readOnly}
            />
            <TextInput
                source="target"
                label="Target"
                multiline
                minRows={2}
                helperText={
                    <>
                        e.g. Children, Adults, S, M, L, 3 yo, 6 yo.
                        <br />
                        For inventory matters, different sizes may need to be
                        listed separately as different supplies.
                    </>
                }
                readOnly={readOnly}
            />
            <TextInput
                source="description"
                label="Description"
                multiline
                minRows={2}
                readOnly={readOnly}
            />
            <Stack direction="row" gap={1}>
                <NumberInput
                    source="price"
                    label="Price (€)"
                    min={0}
                    disabled={!!noPrice || readOnly}
                />
                <BooleanInput
                    source="noPrice"
                    label="Not for sale (no price)"
                    helperText="When enabled, this supply is not for sale and so no price is needed. e.g. items like first-aid kits, tablecloth."
                    readOnly={readOnly}
                />
            </Stack>
            <TextInput
                source="link"
                label="Link"
                helperText="e.g. Link to a website or a PDF file"
                readOnly={readOnly}
            />
            <BooleanInput
                source="enabled"
                label="Enabled"
                defaultValue={true}
                helperText="If disabled, this supply shall not be used anymore during missions."
                readOnly={readOnly}
            />
        </Stack>
    );
};
